/* ==========================================================================
   Podsmania — global.js
   Drawers, sticky header, mega menu, accordions, AJAX cart, predictive
   search, announcement rotation, quantity steppers.

   No dependencies. Every module is defensive: if its markup is absent it
   simply does nothing, so sections can be removed in the theme editor
   without breaking the page.
   ========================================================================== */

(function () {
  "use strict";

  var config = window.Podsmania || {};
  var routes = config.routes || {};
  var strings = config.strings || {};

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function formatMoney(cents) {
    var format = config.moneyFormat || "{{amount}} lei";
    var amount = (cents / 100).toFixed(2);

    // Shopify's money formats differ by placeholder; cover the common ones.
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount.replace(".", ","))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(".", ","))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100).toString());
  }

  /* ========================================================================
     Drawers
     ======================================================================== */

  var Drawers = {
    activeId: null,
    lastFocused: null,

    init: function () {
      document.addEventListener("click", function (event) {
        var opener = event.target.closest("[data-drawer-open]");
        if (opener) {
          var id = opener.getAttribute("data-drawer-open");
          // The cart icon is a real link: only hijack it if the drawer exists.
          if (document.getElementById(id)) {
            event.preventDefault();
            Drawers.open(id, opener);
          }
          return;
        }

        if (event.target.closest("[data-drawer-close]")) {
          event.preventDefault();
          Drawers.close();
        }
      });

      document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && Drawers.activeId) {
          Drawers.close();
        } else if (event.key === "Tab" && Drawers.activeId) {
          Drawers.trapFocus(event);
        }
      });
    },

    open: function (id, trigger) {
      var drawer = document.getElementById(id);
      if (!drawer) return;

      if (Drawers.activeId && Drawers.activeId !== id) Drawers.close();

      Drawers.lastFocused = trigger || document.activeElement;
      Drawers.activeId = id;
      drawer.classList.add("is-open");
      document.body.classList.add("is-locked");

      if (trigger && trigger.hasAttribute("aria-expanded")) {
        trigger.setAttribute("aria-expanded", "true");
      }

      // Wait for the panel transition to start before moving focus, or Safari
      // scrolls the page to the still-offscreen element.
      window.requestAnimationFrame(function () {
        var focusTarget =
          drawer.querySelector("[data-drawer-autofocus]") ||
          drawer.querySelector("input:not([type=hidden]), button, a[href]");
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      });
    },

    close: function () {
      if (!Drawers.activeId) return;
      var drawer = document.getElementById(Drawers.activeId);
      if (drawer) drawer.classList.remove("is-open");

      document.body.classList.remove("is-locked");

      $$('[data-drawer-open="' + Drawers.activeId + '"]').forEach(function (trigger) {
        if (trigger.hasAttribute("aria-expanded")) trigger.setAttribute("aria-expanded", "false");
      });

      Drawers.activeId = null;

      if (Drawers.lastFocused && document.contains(Drawers.lastFocused)) {
        Drawers.lastFocused.focus({ preventScroll: true });
      }
      Drawers.lastFocused = null;
    },

    trapFocus: function (event) {
      var drawer = document.getElementById(Drawers.activeId);
      if (!drawer) return;

      var focusables = $$(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        drawer
      ).filter(function (el) {
        return el.offsetParent !== null;
      });

      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  /* ========================================================================
     Sticky header
     ======================================================================== */

  var StickyHeader = {
    init: function () {
      var header = $("[data-header]");
      if (!header) return;

      var threshold = 12;
      var ticking = false;

      function update() {
        ticking = false;
        header.classList.toggle("is-scrolled", window.scrollY > threshold);
      }

      window.addEventListener(
        "scroll",
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(update);
        },
        { passive: true }
      );

      update();
    }
  };

  /* ========================================================================
     Mega menu
     ======================================================================== */

  var MegaMenu = {
    openPanel: null,
    closeTimer: null,

    init: function () {
      var header = $("[data-header]");
      if (!header) return;

      $$("[data-mega-toggle]").forEach(function (toggle) {
        var panel = document.getElementById(toggle.getAttribute("aria-controls"));
        if (!panel) return;
        var item = toggle.closest(".header__menu-item");

        toggle.addEventListener("click", function (event) {
          event.preventDefault();
          if (MegaMenu.openPanel === panel) MegaMenu.close();
          else MegaMenu.open(toggle, panel, header);
        });

        // Hover intent: open immediately, close on a short delay so moving the
        // pointer diagonally into the panel doesn't dismiss it.
        item.addEventListener("mouseenter", function () {
          if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
          clearTimeout(MegaMenu.closeTimer);
          MegaMenu.open(toggle, panel, header);
        });

        item.addEventListener("mouseleave", function () {
          if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
          MegaMenu.closeTimer = setTimeout(MegaMenu.close, 160);
        });

        panel.addEventListener("mouseenter", function () {
          clearTimeout(MegaMenu.closeTimer);
        });

        panel.addEventListener("mouseleave", function () {
          if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
          MegaMenu.closeTimer = setTimeout(MegaMenu.close, 160);
        });
      });

      document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && MegaMenu.openPanel) {
          var toggle = $('[aria-controls="' + MegaMenu.openPanel.id + '"]');
          MegaMenu.close();
          if (toggle) toggle.focus();
        }
      });

      document.addEventListener("click", function (event) {
        if (!MegaMenu.openPanel) return;
        if (event.target.closest(".header__menu-item") || event.target.closest("[data-mega-panel]")) return;
        MegaMenu.close();
      });
    },

    open: function (toggle, panel, header) {
      if (MegaMenu.openPanel && MegaMenu.openPanel !== panel) MegaMenu.close();

      panel.hidden = false;
      // Force a reflow so the transition runs from the hidden state.
      void panel.offsetWidth;
      panel.classList.add("is-open");
      toggle.setAttribute("aria-expanded", "true");
      header.classList.add("has-mega-open");
      MegaMenu.openPanel = panel;
    },

    close: function () {
      var panel = MegaMenu.openPanel;
      if (!panel) return;

      var toggle = $('[aria-controls="' + panel.id + '"]');
      if (toggle) toggle.setAttribute("aria-expanded", "false");

      panel.classList.remove("is-open");
      var header = $("[data-header]");
      if (header) header.classList.remove("has-mega-open");
      MegaMenu.openPanel = null;

      // Hide only once the fade-out has finished.
      setTimeout(function () {
        if (!panel.classList.contains("is-open")) panel.hidden = true;
      }, 320);
    }
  };

  /* ========================================================================
     Accordions
     ======================================================================== */

  var Accordions = {
    /**
     * The panel is not always the button's next sibling — in the FAQ and the
     * product collapsibles the button sits inside a heading. aria-controls is
     * the reliable link, so resolve through that first.
     */
    panelOf: function (trigger) {
      var id = trigger.getAttribute("aria-controls");
      var panel = id ? document.getElementById(id) : null;
      if (panel) return panel;

      var next = trigger.nextElementSibling;
      if (next && next.classList.contains("accordion__panel")) return next;

      var item = trigger.closest(".accordion__item");
      return item ? item.querySelector(".accordion__panel") : null;
    },

    set: function (trigger, open) {
      trigger.setAttribute("aria-expanded", open ? "true" : "false");
      var panel = Accordions.panelOf(trigger);
      if (panel) panel.classList.toggle("is-open", open);
    },

    /** Panels the template rendered already expanded (the FAQ's "open first"). */
    sync: function () {
      $$("[data-accordion-trigger]").forEach(function (trigger) {
        Accordions.set(trigger, trigger.getAttribute("aria-expanded") === "true");
      });
    },

    init: function () {
      Accordions.sync();

      document.addEventListener("click", function (event) {
        var trigger = event.target.closest("[data-accordion-trigger]");
        if (!trigger) return;

        event.preventDefault();
        var expanded = trigger.getAttribute("aria-expanded") === "true";
        var group = trigger.closest("[data-accordion-single]");

        if (group && !expanded) {
          $$("[data-accordion-trigger]", group).forEach(function (other) {
            if (other !== trigger) Accordions.set(other, false);
          });
        }

        Accordions.set(trigger, !expanded);
      });
    }
  };

  /* ========================================================================
     Cart
     ======================================================================== */

  var Cart = {
    init: function () {
      document.addEventListener("submit", function (event) {
        // `{% form 'product' %}` cannot take a hyphenated data attribute, so
        // the product form is matched by its class instead.
        var form = event.target.closest("[data-quick-add-form], form.product-form");
        if (!form) return;
        event.preventDefault();
        Cart.add(form);
      });

      document.addEventListener("click", function (event) {
        var removeBtn = event.target.closest("[data-cart-remove]");
        if (removeBtn) {
          event.preventDefault();
          Cart.change(removeBtn.getAttribute("data-line"), 0);
          return;
        }

        var checkoutBtn = event.target.closest("[data-checkout-button]");
        if (checkoutBtn && window.PodsmaniaTikTok) {
          window.PodsmaniaTikTok.track("InitiateCheckout", {
            content_type: "product",
            value: parseFloat(checkoutBtn.getAttribute("data-cart-total") || "0"),
            currency: checkoutBtn.getAttribute("data-currency") || "RON"
          });
        }
      });

      document.addEventListener("change", function (event) {
        var input = event.target.closest("[data-cart-quantity]");
        if (!input) return;
        Cart.change(input.getAttribute("data-line"), parseInt(input.value, 10));
      });
    },

    add: function (form) {
      var button = form.querySelector("[data-add-to-cart]");
      var label = form.querySelector("[data-add-to-cart-text]");
      var original = label ? label.textContent : "";

      if (button) {
        button.setAttribute("data-loading", "true");
        button.disabled = true;
      }

      fetch(routes.cartAdd, {
        method: "POST",
        headers: { Accept: "application/javascript", "X-Requested-With": "XMLHttpRequest" },
        body: new FormData(form)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (item) {
          if (label) label.textContent = strings.addedToCart || "Adăugat";

          if (window.PodsmaniaTikTok) {
            window.PodsmaniaTikTok.track("AddToCart", {
              content_type: "product",
              content_id: item.variant_id || item.id,
              content_name: item.product_title || item.title,
              quantity: item.quantity || 1,
              price: (item.final_price || item.price || 0) / 100,
              value: ((item.final_price || item.price || 0) * (item.quantity || 1)) / 100,
              currency: (config.cart && config.cart.currency) || "RON"
            });
          }

          return Cart.refresh(true);
        })
        .catch(function (error) {
          var message = (error && error.description) || strings.cartError;
          Cart.notify(form, message);
        })
        .finally(function () {
          setTimeout(function () {
            if (button) {
              button.removeAttribute("data-loading");
              button.disabled = false;
            }
            if (label) label.textContent = original;
          }, 1200);
        });
    },

    change: function (line, quantity) {
      if (!line) return;

      fetch(routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (response) {
          return response.json();
        })
        .then(function () {
          return Cart.refresh(false);
        })
        .catch(function () {
          window.location.href = routes.cart;
        });
    },

    /**
     * Pulls fresh markup through Shopify's Section Rendering API so the
     * drawer, the count bubble and the cart page all stay in sync without a
     * full page reload.
     */
    refresh: function (openDrawer) {
      var sectionIds = [];
      var drawer = $("[data-cart-drawer-section]");
      var cartPage = $("[data-cart-page-section]");

      if (drawer) sectionIds.push(drawer.getAttribute("data-cart-drawer-section"));
      if (cartPage) sectionIds.push(cartPage.getAttribute("data-cart-page-section"));

      var url = routes.cart + ".js";

      return fetch(url, { headers: { Accept: "application/json" } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          Cart.updateCount(cart.item_count);

          if (!sectionIds.length) {
            if (openDrawer) window.location.href = routes.cart;
            return cart;
          }

          return fetch(routes.cart + "?sections=" + sectionIds.join(","))
            .then(function (response) {
              return response.json();
            })
            .then(function (sections) {
              Object.keys(sections).forEach(function (id) {
                var parsed = new DOMParser().parseFromString(sections[id], "text/html");
                var incoming = parsed.querySelector("[data-cart-drawer-section], [data-cart-page-section]");
                var target =
                  $('[data-cart-drawer-section="' + id + '"]') ||
                  $('[data-cart-page-section="' + id + '"]');
                if (incoming && target) target.innerHTML = incoming.innerHTML;
              });

              if (window.PodsmaniaMotion) window.PodsmaniaMotion.refresh(document);
              if (openDrawer && config.cart && config.cart.type === "drawer") {
                Drawers.open("CartDrawer");
              }
              return cart;
            });
        });
    },

    updateCount: function (count) {
      $$("[data-cart-count]").forEach(function (el) {
        el.textContent = count;
        el.classList.toggle("is-empty", count === 0);
        el.classList.remove("is-bumped");
        void el.offsetWidth;
        el.classList.add("is-bumped");
      });
    },

    notify: function (form, message) {
      var existing = form.querySelector("[data-cart-error]");
      if (existing) existing.remove();

      var node = document.createElement("p");
      node.className = "form-message form-message--error";
      node.setAttribute("data-cart-error", "");
      node.setAttribute("role", "alert");
      node.textContent = message;
      form.appendChild(node);

      setTimeout(function () {
        node.remove();
      }, 5000);
    }
  };

  /* ========================================================================
     Predictive search
     ======================================================================== */

  var PredictiveSearch = {
    controller: null,

    init: function () {
      var input = $("[data-predictive-input]");
      var results = $("[data-predictive-results]");
      if (!input || !results) return;

      var debounce = null;

      input.addEventListener("input", function () {
        clearTimeout(debounce);
        var term = input.value.trim();

        if (term.length < 2) {
          results.innerHTML = "";
          return;
        }

        debounce = setTimeout(function () {
          PredictiveSearch.query(term, results);
        }, 220);
      });
    },

    query: function (term, results) {
      // Cancel the previous request so out-of-order responses can't overwrite
      // newer ones.
      if (PredictiveSearch.controller) PredictiveSearch.controller.abort();
      PredictiveSearch.controller = new AbortController();

      var url =
        routes.predictiveSearch +
        "?q=" +
        encodeURIComponent(term) +
        "&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search";

      fetch(url, { signal: PredictiveSearch.controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error("search failed");
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, "text/html");
          var content = parsed.querySelector("[data-predictive-content]");
          results.innerHTML = content ? content.innerHTML : "";
        })
        .catch(function (error) {
          if (error.name === "AbortError") return;
          results.innerHTML =
            '<p class="predictive__empty">' + (strings.searchNoResults || "") + "</p>";
        });
    }
  };

  /* ========================================================================
     Announcement rotator
     ======================================================================== */

  var Announcement = {
    init: function () {
      $$("[data-announcement]").forEach(function (bar) {
        var slides = $$("[data-announcement-slide]", bar);
        var current = 0;
        var timer = null;

        var closeBtn = $("[data-announcement-close]", bar);
        if (closeBtn) {
          closeBtn.addEventListener("click", function () {
            bar.style.display = "none";
            try {
              sessionStorage.setItem("podsmania:announcement-dismissed", "1");
            } catch (error) {
              // Private browsing — dismissal just won't persist.
            }
          });

          try {
            if (sessionStorage.getItem("podsmania:announcement-dismissed") === "1") {
              bar.style.display = "none";
            }
          } catch (error) {
            // Storage unavailable; show the bar.
          }
        }

        if (slides.length < 2) return;

        function show(index) {
          slides.forEach(function (slide, i) {
            var active = i === index;
            slide.classList.toggle("is-active", active);
            if (active) slide.removeAttribute("aria-hidden");
            else slide.setAttribute("aria-hidden", "true");
          });
          current = index;
        }

        function next() {
          show((current + 1) % slides.length);
        }

        function prev() {
          show((current - 1 + slides.length) % slides.length);
        }

        var nextBtn = $("[data-announcement-next]", bar);
        var prevBtn = $("[data-announcement-prev]", bar);
        if (nextBtn) nextBtn.addEventListener("click", function () { next(); restart(); });
        if (prevBtn) prevBtn.addEventListener("click", function () { prev(); restart(); });

        var interval = parseInt(bar.getAttribute("data-autorotate") || "0", 10);

        function restart() {
          clearInterval(timer);
          if (interval > 0) timer = setInterval(next, interval);
        }

        restart();

        // Don't rotate copy out from under someone who is reading it.
        bar.addEventListener("mouseenter", function () { clearInterval(timer); });
        bar.addEventListener("mouseleave", restart);
      });
    }
  };

  /* ========================================================================
     Quantity steppers
     ======================================================================== */

  var Quantity = {
    init: function () {
      document.addEventListener("click", function (event) {
        var btn = event.target.closest("[data-qty-change]");
        if (!btn) return;

        event.preventDefault();
        var wrapper = btn.closest(".qty");
        var input = wrapper ? wrapper.querySelector("input") : null;
        if (!input) return;

        var step = btn.getAttribute("data-qty-change") === "up" ? 1 : -1;
        var min = parseInt(input.getAttribute("min") || "1", 10);
        var max = parseInt(input.getAttribute("max") || "9999", 10);
        var value = parseInt(input.value, 10) || min;

        input.value = Math.min(max, Math.max(min, value + step));
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
  };

  /* ========================================================================
     Destructive-action confirmation
     Guards anything carrying data-confirm-message (deleting an address).
     ======================================================================== */

  var Confirm = {
    init: function () {
      document.addEventListener("click", function (event) {
        var trigger = event.target.closest("[data-confirm-message]");
        if (!trigger) return;

        var message = trigger.getAttribute("data-confirm-message");
        if (message && !window.confirm(message)) {
          event.preventDefault();
          event.stopPropagation();
        }
      });
    }
  };

  /* ========================================================================
     Boot
     ======================================================================== */

  function init() {
    Confirm.init();
    Drawers.init();
    StickyHeader.init();
    MegaMenu.init();
    Accordions.init();
    Cart.init();
    PredictiveSearch.init();
    Announcement.init();
    Quantity.init();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // The theme editor swaps section markup in place; re-bind the modules that
  // hold direct element references rather than delegating from document.
  document.addEventListener("shopify:section:load", function () {
    StickyHeader.init();
    MegaMenu.init();
    Announcement.init();
    // Only the state sync — the click handler is delegated from document and
    // binding it again here would toggle every panel twice.
    Accordions.sync();
  });

  window.PodsmaniaCart = Cart;
  window.PodsmaniaDrawers = Drawers;
  window.PodsmaniaMoney = formatMoney;
})();
